/**
 * Campaign status values
 */
export const CAMPAIGN_STATUS = {
  PENDING: "pending",
  SCHEDULED: "scheduled",
  ENDED: "ended",
  DRAFT: "draft",
};

/**
 * Campaign contact types
 */
export const CAMPAIGN_CONTACT_TYPES = {
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp",
  NOTIFICATION: "notification",
  POPUP: "popup",
  WEBHOOK: "webhook",
};

/**
 * Campaign audience types
 */
export const CAMPAIGN_AUDIENCE_TYPES = {
  FIXED: "fixed",
  DYNAMIC: "dynamic",
};

/**
 * Status colors for campaigns
 */
export const CAMPAIGN_STATUS_COLORS = {
  [CAMPAIGN_STATUS.ENDED]: {
    bg: "bg-green-500/20",
    text: "text-green-400",
    dot: "#3DDC97",
  },
  [CAMPAIGN_STATUS.SCHEDULED]: {
    bg: "bg-yellow-500/20",
    text: "text-yellow-400",
    dot: "#FFC700",
  },
  [CAMPAIGN_STATUS.PENDING]: {
    bg: "bg-blue-500/20",
    text: "text-blue-400",
    dot: "#4A90E2",
  },
  [CAMPAIGN_STATUS.DRAFT]: {
    bg: "bg-gray-500/20",
    text: "text-gray-400",
    dot: "#6B7280",
  },
};

/**
 * Contact type colors
 */
export const CONTACT_TYPE_COLORS = {
  [CAMPAIGN_CONTACT_TYPES.EMAIL]: {
    bg: "bg-blue-500/20",
    text: "text-blue-400",
    icon: "Mail",
  },
  [CAMPAIGN_CONTACT_TYPES.SMS]: {
    bg: "bg-green-500/20",
    text: "text-green-400",
    icon: "MessageSquare",
  },
  [CAMPAIGN_CONTACT_TYPES.WHATSAPP]: {
    bg: "bg-emerald-500/20",
    text: "text-emerald-400",
    icon: "MessageCircle",
  },
  [CAMPAIGN_CONTACT_TYPES.NOTIFICATION]: {
    bg: "bg-purple-500/20",
    text: "text-purple-400",
    icon: "Bell",
  },
  [CAMPAIGN_CONTACT_TYPES.POPUP]: {
    bg: "bg-orange-500/20",
    text: "text-orange-400",
    icon: "Monitor",
  },
  [CAMPAIGN_CONTACT_TYPES.WEBHOOK]: {
    bg: "bg-cyan-500/20",
    text: "text-cyan-400",
    icon: "Webhook",
  },
};

/**
 * Filter tabs for campaigns
 */
export const CAMPAIGN_FILTER_TABS = [
  {
    key: "all",
    label: "All Campaigns",
    labelKey: "campaigns.filters.tabs.all",
    color: "text-blue-400",
  },
  {
    key: CAMPAIGN_CONTACT_TYPES.EMAIL,
    label: "Email",
    labelKey: "campaigns.filters.tabs.email",
    color: "text-blue-400",
  },
  {
    key: CAMPAIGN_CONTACT_TYPES.SMS,
    label: "SMS",
    labelKey: "campaigns.filters.tabs.sms",
    color: "text-green-400",
  },
  {
    key: CAMPAIGN_CONTACT_TYPES.WHATSAPP,
    label: "WhatsApp",
    labelKey: "campaigns.filters.tabs.whatsapp",
    color: "text-emerald-400",
  },
  {
    key: CAMPAIGN_CONTACT_TYPES.NOTIFICATION,
    label: "Notification",
    labelKey: "campaigns.filters.tabs.notification",
    color: "text-purple-400",
  },
];

/**
 * Status filter options
 */
export const CAMPAIGN_STATUS_OPTIONS = [
  {
    value: "all",
    label: "All Status",
    labelKey: "campaigns.filters.status.all",
  },
  {
    value: CAMPAIGN_STATUS.PENDING,
    label: "Pending",
    labelKey: "campaigns.filters.status.pending",
  },
  {
    value: CAMPAIGN_STATUS.SCHEDULED,
    label: "Scheduled",
    labelKey: "campaigns.filters.status.scheduled",
  },
  {
    value: CAMPAIGN_STATUS.ENDED,
    label: "Ended",
    labelKey: "campaigns.filters.status.sent",
  },
];

/**
 * Pagination constants
 */
export const CAMPAIGNS_PER_PAGE = 10;
export const MAX_VISIBLE_PAGES = 5;

/**
 * Get status badge info
 */
export const getCampaignStatusBadge = (status) => {
  const colors =
    CAMPAIGN_STATUS_COLORS[status] ||
    CAMPAIGN_STATUS_COLORS[CAMPAIGN_STATUS.DRAFT];
  const key = status || CAMPAIGN_STATUS.DRAFT;
  return {
    label: status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown",
    labelKey: `campaigns.status.${key}`,
    ...colors,
  };
};

/**
 * Get contact type badge info
 */
export const getContactTypeBadge = (contactType) => {
  const colors = CONTACT_TYPE_COLORS[contactType] || {
    bg: "bg-gray-500/20",
    text: "text-gray-400",
    icon: "HelpCircle",
  };
  const normalizedType = (contactType || "unknown").toLowerCase();
  return {
    label:
      contactType?.charAt(0).toUpperCase() + contactType?.slice(1) || "Unknown",
    labelKey: `campaigns.table.fallback.${normalizedType}`,
    ...colors,
  };
};

/**
 * Format campaign name
 */
export const getCampaignName = (campaign) => {
  return campaign?.name || "Untitled Campaign";
};

/**
 * Get campaign in queue count
 */
export const getCampaignInQueue = (campaign) => {
  const audience = campaign?.audience_count || 0;
  const sent = campaign?.sent_count || 0;
  return Math.max(0, audience - sent);
};

/**
 * Check if campaign stats are available (not SMS or push)
 */
export const hasDetailedStats = (contactType) => {
  return contactType !== CAMPAIGN_CONTACT_TYPES.SMS && contactType !== "push";
};

/**
 * Campaign status display configuration (for UI badges)
 */
export const CAMPAIGN_STATUS_DISPLAY = {
  ended: {
    label: "Sent",
    labelKey: "campaigns.status.sent",
    bgColor: "bg-green-500",
    textColor: "text-white",
  },
  scheduled: {
    label: "Scheduled",
    labelKey: "campaigns.status.scheduled",
    bgColor: "bg-blue-500",
    textColor: "text-white",
  },
  pending: {
    label: "Active",
    labelKey: "campaigns.status.active",
    bgColor: "bg-yellow-500",
    textColor: "text-black",
  },
  draft: {
    label: "Draft",
    labelKey: "campaigns.status.draft",
    bgColor: "bg-gray-500",
    textColor: "text-white",
  },
};

/**
 * Get campaign status display configuration
 * @param {string} status - Campaign status
 * @returns {Object} Status display configuration { label, bgColor, textColor }
 */
export const getCampaignStatusDisplay = (status) => {
  return (
    CAMPAIGN_STATUS_DISPLAY[status] || {
      label: status,
      bgColor: "bg-gray-500",
      textColor: "text-white",
    }
  );
};

/**
 * Strip HTML tags from a string (useful for campaign body preview)
 * @param {string} html - HTML string to strip
 * @returns {string} Plain text without HTML tags
 */
export const stripHtml = (html) => {
  if (!html) return "";
  // Remove button/link elements (they're shown separately in preview)
  let cleanHtml = html.replace(/<p[^>]*>\s*<a[^>]*>.*?<\/a>\s*<\/p>/gi, "");

  const tmp = document.createElement("div");
  tmp.innerHTML = cleanHtml
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n");
  return (tmp.textContent || tmp.innerText || "").trim();
};

/**
 * Extract button data from campaign HTML body
 * @param {string} html - HTML string containing button
 * @returns {Object} Button data with text, color, and url
 */
export const extractButtonData = (html) => {
  if (!html) return null;

  // Match button HTML pattern: <a href='url'><button style='...background: color...'>text</button></a>
  const buttonMatch = html.match(
    /<a\s+href=['"]([^'"]+)['"][^>]*>\s*<button\s+style=['"][^'"]*background:\s*([^;'"]+)[^'"]*['"][^>]*>([^<]+)<\/button>\s*<\/a>/i,
  );

  if (buttonMatch) {
    let extractedUrl = buttonMatch[1];

    // If the URL is a tracking URL with a "next" parameter, extract the actual destination URL
    if (extractedUrl.includes("?next=")) {
      try {
        const urlObj = new URL(extractedUrl);
        const nextParam = urlObj.searchParams.get("next");
        if (nextParam) {
          extractedUrl = decodeURIComponent(nextParam);
        }
      } catch (e) {
        // If URL parsing fails, keep the original URL
        console.warn("Failed to parse tracking URL:", e);
      }
    }

    return {
      url: extractedUrl,
      color: buttonMatch[2].trim(),
      text: buttonMatch[3].trim(),
    };
  }

  return null;
};

/**
 * Get relative time ago string from a date
 * @param {string|Date} dateString - The date to format
 * @param {Object} options - Formatting options
 * @param {string} options.prefix - Prefix to add (default: '')
 * @param {string} options.fallback - Fallback text for invalid dates (default: '')
 * @returns {string} Relative time string (e.g., "5 mins ago", "2 hours ago")
 */
export const getTimeAgo = (dateString, options = {}) => {
  const { prefix = "", fallback = "" } = options;

  if (!dateString) return fallback;

  // Handle UTC dates from API
  // If the date string doesn't have timezone info, assume it's UTC
  let date;
  if (
    dateString.includes("T") ||
    dateString.includes("Z") ||
    dateString.includes("+")
  ) {
    // Already has timezone info
    date = new Date(dateString);
  } else {
    // No timezone info, assume UTC and convert to local
    date = new Date(dateString + " UTC");
  }

  if (isNaN(date.getTime())) return fallback;

  const now = new Date();
  const diffMs = now - date;

  if (diffMs < 0) {
    if (diffMs > -300000) {
      return prefix ? `${prefix} just now` : "just now";
    }
    const absDiffMs = Math.abs(diffMs);
    const diffMins = Math.floor(absDiffMs / 60000);
    const diffHours = Math.floor(absDiffMs / 3600000);
    const diffDays = Math.floor(absDiffMs / 86400000);

    let result;
    if (diffMins < 60) {
      result = `${diffMins} min${diffMins === 1 ? "" : "s"} ago`;
    } else if (diffHours < 24) {
      result = `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    } else if (diffDays === 1) {
      result = "Yesterday";
    } else {
      result = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    }
    return prefix ? `${prefix} ${result}` : result;
  }

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let result;
  if (diffMins < 1) {
    result = "just now";
  } else if (diffMins < 60) {
    result = `${diffMins} min${diffMins === 1 ? "" : "s"} ago`;
  } else if (diffHours < 24) {
    result = `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  } else if (diffDays === 1) {
    result = "Yesterday";
  } else {
    result = `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  return prefix ? `${prefix} ${result}` : result;
};

export default {
  CAMPAIGN_STATUS,
  CAMPAIGN_CONTACT_TYPES,
  CAMPAIGN_AUDIENCE_TYPES,
  CAMPAIGN_STATUS_COLORS,
  CONTACT_TYPE_COLORS,
  CAMPAIGN_FILTER_TABS,
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGNS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  getCampaignStatusBadge,
  getContactTypeBadge,
  getCampaignName,
  getCampaignInQueue,
  hasDetailedStats,
  CAMPAIGN_STATUS_DISPLAY,
  getCampaignStatusDisplay,
  stripHtml,
  getTimeAgo,
};
